// The teacher-management callables.
//
// Every one of these is authorised on the server against a backend allowlist.
// The dashboard's isAdmin decides only what is drawn; it grants nothing, and a
// non-admin who reached these would simply be refused.
//
// No function here stores anything. An issued code lives in React state for as
// long as the panel is open and nowhere else — not in a URL, not in
// localStorage, not in sessionStorage.

import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase'
import { BILLING_MODE } from './access'

const call = (name) => httpsCallable(functions, name)

const MESSAGES = {
  'permission-denied': 'This account is not permitted to manage teachers.',
  'unauthenticated': 'Please sign in again.',
  'already-exists': 'That referral id is already in use. Choose another.',
  'not-found': 'That code was not found.',
  'failed-precondition': 'That code has been redeemed. Revoke the teacher’s access instead.',
  'invalid-argument': 'Please check the details and try again.',
}

export function adminErrorMessage(err) {
  const code = String(err?.code || '').replace(/^functions\//, '')
  return MESSAGES[code] || 'Something went wrong. Please try again.'
}

/**
 * Whether a failure left the outcome unknown.
 *
 * A refusal is definitive: nothing was written. A network or internal failure
 * is not — the write may have committed while the reply was lost. The caller
 * must reload rather than retry, or it risks a second live code nobody has
 * seen.
 */
export function isAmbiguousFailure(err) {
  const code = String(err?.code || '').replace(/^functions\//, '')
  return !['permission-denied', 'unauthenticated', 'already-exists', 'not-found',
    'failed-precondition', 'invalid-argument'].includes(code)
}

export async function listTeachers() {
  const { data } = await call('accountListTeachers')({ mode: BILLING_MODE })
  return data
}

export async function issueTeacherCode(fields) {
  const { data } = await call('accountIssueTeacherCode')({ mode: BILLING_MODE, ...fields })
  return data
}

export async function voidTeacherCode(codeHash) {
  const { data } = await call('accountVoidTeacherCode')({ mode: BILLING_MODE, codeHash })
  return data
}

export async function revokeTeacher(uid, label) {
  const { data } = await call('accountRevokeTeacher')({ mode: BILLING_MODE, uid, label })
  return data
}

/**
 * Copy to the clipboard, reporting whether it worked.
 *
 * navigator.clipboard rejects on an insecure origin and when permission is
 * refused, so the result is returned rather than assumed — a UI that claims a
 * copy succeeded when it did not invites an administrator to dismiss the only
 * copy of a code.
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
